import { useCallback, useEffect, useRef, useState } from "react";
import { VoiceEngine } from "./voice-engine.ts";

/** One audio file to hear on its own (a recording's track, a track being lined up). */
export interface SoloTrack {
  id: string;
  url: string;
  contentType: string;
  bytes: number;
  /** Known length, when the list already says it (bot tracks). */
  duration?: number;
}

export interface TrackPlayer {
  /** The track loaded, if any. */
  id: string | null;
  playing: boolean;
  /** Where it is, in its own seconds. */
  at: number;
  duration: number | null;
  /** Loads `track` (if another one) and plays it from `from`, or from where it was. */
  play: (track: SoloTrack, from?: number) => void;
  pause: () => void;
  /** Moves without changing play/pause. */
  seek: (seconds: number) => void;
  toggle: (track: SoloTrack) => void;
  /** Loads `track` (if another one) and puts it at `seconds`, paused. */
  cue: (track: SoloTrack, seconds: number) => void;
}

/**
 * A player for one track at a time, on the same engine as the replay's voice
 * (Web Audio, so Discord recordings play smoothly on iPhone): the engine is
 * driven by a clock of its own instead of the replay's. Not the browser's
 * player: its controls differ per browser and it cannot play Ogg on some.
 */
export function useTrackPlayer(): TrackPlayer {
  const engine = useRef<VoiceEngine | null>(null);
  const [track, setTrack] = useState<SoloTrack | null>(null);
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(0);
  const [durations, setDurations] = useState<Record<string, number>>({});
  // The clock: `base` seconds of the track at `since` (performance.now()); stopped when not playing.
  const clock = useRef({ base: 0, since: 0, playing: false });

  useEffect(() => {
    const e = new VoiceEngine((id, seconds) => setDurations((d) => (d[id] === seconds ? d : { ...d, [id]: seconds })));
    engine.current = e;
    return () => {
      e.dispose();
      engine.current = null;
    };
  }, []);

  const now = () => (clock.current.playing ? clock.current.base + (performance.now() - clock.current.since) / 1000 : clock.current.base);
  const duration = track ? (durations[track.id] ?? track.duration ?? null) : null;

  // Each frame while playing: tell the engine where the track should be, and stop at its end.
  useEffect(() => {
    if (!track) return;
    let frame = 0;
    let shown = -1;
    const tick = () => {
      let t = now();
      if (duration !== null && t >= duration) {
        clock.current = { base: duration, since: 0, playing: false };
        setPlaying(false);
        t = duration;
      }
      engine.current?.sync(track.id, { target: t, play: clock.current.playing, speed: 1, volume: 1 });
      // A tenth of a second is all the screen shows.
      const rounded = Math.floor(t * 10);
      if (rounded !== shown) {
        shown = rounded;
        setAt(t);
      }
      if (clock.current.playing) frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
    // `playing` restarts the loop; the clock itself lives in the ref.
  }, [track, playing, duration]);

  const load = useCallback((next: SoloTrack) => {
    engine.current?.setTracks([{ id: next.id, url: next.url, contentType: next.contentType, bytes: next.bytes }]);
    setTrack(next);
  }, []);

  const play = useCallback(
    (next: SoloTrack, from?: number) => {
      const same = track?.id === next.id;
      if (!same) load(next);
      const start = from ?? (same ? now() : 0);
      clock.current = { base: Math.max(0, start), since: performance.now(), playing: true };
      setAt(clock.current.base);
      setPlaying(true);
    },

    [track, load],
  );

  const pause = useCallback(() => {
    clock.current = { base: now(), since: 0, playing: false };
    setPlaying(false);
  }, []);

  const seek = useCallback(
    (seconds: number) => {
      const t = Math.max(0, duration !== null ? Math.min(seconds, duration) : seconds);
      clock.current = { base: t, since: performance.now(), playing: clock.current.playing };
      setAt(t);
      if (track) engine.current?.sync(track.id, { target: t, play: clock.current.playing, speed: 1, volume: 1 });
    },
    [duration, track],
  );

  const toggle = useCallback(
    (next: SoloTrack) => {
      if (track?.id === next.id && playing) pause();
      else play(next, track?.id === next.id && duration !== null && now() >= duration ? 0 : undefined);
    },

    [track, playing, duration, play, pause],
  );

  const cue = useCallback(
    (next: SoloTrack, seconds: number) => {
      if (track?.id !== next.id) load(next);
      clock.current = { base: Math.max(0, seconds), since: 0, playing: false };
      setPlaying(false);
      setAt(clock.current.base);
    },
    [track, load],
  );

  return { id: track?.id ?? null, playing, at, duration, play, pause, seek, toggle, cue };
}
